import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { t } from 'i18next';
import { logger } from '@sentry/react';
import { useUpdateNodeInternals } from 'reactflow';
import { useQuery } from '@tanstack/react-query';
import { Flex, FlexCenHorVer } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { I18N_KEYS } from '@/language/keys';
import { LinkIcon } from '@/UI/Icons';
import { LoadingCircle } from '@/UI/Animations/LoadingCircle';
import { useGlobalStore } from '@/state/global.state';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { ImageFileViewer, NodeViewer } from '../Shared/FileViewer';
import { useFigmaCompositorView, useOnInputsChange } from '../../Recipe/FlowContext';
import { getOrCreateFigmaApiClient } from '../../Recipe/FlowComponents/Editor/FigmaCompositor/figmaApiClient';
import { getFigmaFileUrl, getFormattedNodeId, useSyncToFigma } from './utils';
import type { FigmaCompositorData } from '@/types/node';

function LoadingState({ isOverlay = false }: { isOverlay?: boolean } = {}) {
  return (
    <FlexCenHorVer
      className={isOverlay ? '' : 'media-container-dark'}
      sx={
        isOverlay
          ? { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }
          : { width: '100%', height: '200px' }
      }
    >
      <LoadingCircle size={32} color="white" />
    </FlexCenHorVer>
  );
}

function Footer({
  id,
  handleOpenEditWindow,
  handleRefresh,
  handleAddInputHandle,
}: {
  id: string;
  handleOpenEditWindow: () => void;
  handleRefresh: () => void;
  handleAddInputHandle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid={`open-compositor-edit-button-container-${id}`}
      sx={{
        opacity: 1,
        pointerEvents: 'auto',
        cursor: 'auto',
      }}
    >
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonContained mode="text" size="small" onClick={handleAddInputHandle}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.COMPOSITOR.ADD_ANOTHER_LAYER_CTA)}
        </ButtonContained>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', marginLeft: 1 }}>
          <ButtonContained
            mode="outlined"
            onClick={handleRefresh}
            sx={{ height: '36px', borderRadius: 2, px: 1.5, py: 1, marginRight: 1 }}
          >
            {t(I18N_KEYS.FIGMA_COMPOSITOR.REFRESH)}
          </ButtonContained>
          <ButtonContained
            mode="outlined"
            onClick={handleOpenEditWindow}
            sx={{ height: '36px', borderRadius: 2, px: 1.5, py: 1 }}
            startIcon={<LinkIcon height="16px" width="16px" />}
          >
            {t(I18N_KEYS.FIGMA_COMPOSITOR.OPEN_IN_FIGMA)}
          </ButtonContained>
        </Flex>
      </Flex>
    </Box>
  );
}

function FigmaCompositorContent({
  id,
  data,
  handleAddInputHandle,
  handleOpenEditWindow,
  handleRefresh,
  isInitialImagePreviewLoading,
  isImagePreviewUpdating,
}: {
  id: string;
  data: FigmaCompositorData;
  handleOpenEditWindow: () => void;
  handleRefresh: () => void;
  handleAddInputHandle: () => void;
  isInitialImagePreviewLoading: boolean;
  isImagePreviewUpdating: boolean;
}) {
  const outputImage = data.outputImage;

  let nodeViewer: React.ReactNode;
  if (isInitialImagePreviewLoading) {
    nodeViewer = <LoadingState />;
  } else if (outputImage) {
    nodeViewer = (
      <ImageFileViewer
        url={outputImage.url}
        thumbnailUrl={outputImage.thumbnailUrl}
        width={outputImage.width}
        height={outputImage.height}
      />
    );
  } else {
    nodeViewer = <NodeViewer id={id} />;
  }

  return (
    <>
      <Flex sx={{ cursor: 'default', position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        {nodeViewer}
        {isImagePreviewUpdating && <LoadingState isOverlay />}
      </Flex>
      <Footer
        id={id}
        handleOpenEditWindow={handleOpenEditWindow}
        handleRefresh={handleRefresh}
        handleAddInputHandle={handleAddInputHandle}
      />
    </>
  );
}

export function FigmaCompositorNode({ id, data }: { id: string; data: FigmaCompositorData }) {
  const view = useFigmaCompositorView(id);
  const { updateSnackbarData } = useGlobalStore();
  const updateNodeInternals = useUpdateNodeInternals();

  const { fileKey, nodeId: figmaNodeId } = data.options;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    isFetching: isImagePreviewFetching,
    data: previewData,
    isError: isImagePreviewError,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ['figma-compositor-preview', fileKey, figmaNodeId],
    queryFn: () => getOrCreateFigmaApiClient().fetchFigmaNodePreview(fileKey!, figmaNodeId!),
    enabled: !!(fileKey && figmaNodeId),
    refetchOnWindowFocus: false,
    retry: 0,
    // Treat stored outputImage as initial data so no loading flash on reload.
    // initialDataUpdatedAt: 0 marks it stale immediately, triggering a background refetch.
    initialData: data.outputImage ?? undefined,
    initialDataUpdatedAt: 0,
  });

  useEffect(() => {
    if (previewData) {
      view.setOutputImage(previewData);
    }
  }, [previewData, view]);

  // Show snackbar only on first error
  useEffect(() => {
    if (isImagePreviewError) {
      logger.error('Cannot load Figma node image preview');
      updateSnackbarData({
        text: `${t(I18N_KEYS.FIGMA_COMPOSITOR.LOAD_IMAGE_ERROR)}`,
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    }
  }, [isImagePreviewError, updateSnackbarData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchPreview();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenEditWindow = () => {
    const formattedNodeId = getFormattedNodeId(data.options.nodeId || '');
    window.open(getFigmaFileUrl(data?.options?.fileKey || '', formattedNodeId), '_blank');
  };

  const handleAddInputHandle = () => {
    view.addNodeInputHandle();
    updateNodeInternals(id);
  };

  const triggerSyncToFigma = useSyncToFigma(view, data.options, setIsRefreshing);
  useOnInputsChange(id, triggerSyncToFigma);

  return (
    <DynamicNode2 id={id} data={data}>
      <FigmaCompositorContent
        id={id}
        data={data}
        handleRefresh={() => void handleRefresh()}
        isInitialImagePreviewLoading={isImagePreviewFetching && !previewData}
        isImagePreviewUpdating={(isImagePreviewFetching && !!previewData) || isRefreshing}
        handleOpenEditWindow={handleOpenEditWindow}
        handleAddInputHandle={handleAddInputHandle}
      />
    </DynamicNode2>
  );
}
